import React, { useState, useEffect } from "react";
import { X, Plus, Minus } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface CommissionAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    salesRepCommission: number;
    excessAmount: number;
    adjustmentAmount: number;
    excessAdjustmentAmount: number;
  }) => Promise<void>;
  amounts: {
    salesRepCommission?: number;
    excessAmount?: number;
    excessAdjustmentAmount?: number;
    adjustmentAmount?: number;
  };
  isLoading?: boolean;
}

type AdjustmentType = "addition" | "subtraction";

const CommissionAdjustmentModal: React.FC<CommissionAdjustmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  amounts = {
    salesRepCommission: 0,
    excessAdjustmentAmount: 0,
    excessAmount: 0,
    adjustmentAmount: 0,
  },
  isLoading = false,
}) => {
  const [adjustmentType, setAdjustmentType] =
    useState<AdjustmentType>("addition");
  const [adjustValue, setAdjustValue] = useState<string>("");
  const [salesRepCommission, setSalesRepCommission] = useState<string>("");
  const [excessAmount, setExcessAmount] = useState<string>("");
  const [error, setError] = useState("");
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>("");

  const handleWheelPrevent = usePreventWheelChange();

  useEffect(() => {
    if (isOpen) {
      setSalesRepCommission(
        amounts.salesRepCommission ? amounts.salesRepCommission.toString() : "",
      );
      setAdjustmentAmount(
        amounts.adjustmentAmount ? amounts.adjustmentAmount.toString() : "",
      );
      setExcessAmount(
        amounts.excessAmount ? amounts.excessAmount.toString() : "",
      );
      if (amounts.excessAdjustmentAmount) {
        if (amounts.excessAdjustmentAmount < 0) {
          setAdjustmentType("subtraction");
          setAdjustValue(Math.abs(amounts.excessAdjustmentAmount).toString());
        } else if (amounts.excessAdjustmentAmount > 0) {
          setAdjustmentType("addition");
          setAdjustValue(amounts.excessAdjustmentAmount.toString());
        } else {
          setAdjustmentType("addition");
          setAdjustValue("");
        }
      }
      setError("");
    }
  }, [isOpen, amounts]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const numValueAdjustment = parseFloat(adjustValue || "0");
    const numSalesRepCommission = parseFloat(salesRepCommission || "0");
    const numExcessAmount = parseFloat(excessAmount || "0");
    const numAdjustmentAmount = parseFloat(adjustmentAmount || "0");
    const finalAdjustmentAmount =
      adjustmentType === "addition" ? numValueAdjustment : -numValueAdjustment;

    await onSubmit({
      salesRepCommission: numSalesRepCommission,
      excessAmount: numExcessAmount,
      adjustmentAmount: numAdjustmentAmount,
      excessAdjustmentAmount: finalAdjustmentAmount,
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">
            Adjust Commission
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
            disabled={isLoading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sales Rep Commission (₹)
            </label>

            <input
              type="number"
              value={salesRepCommission}
              onChange={(e) => setSalesRepCommission(e.target.value)}
              onWheel={handleWheelPrevent}
              placeholder="Enter sales rep commission"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border-gray-300"
              step="1"
              min="0"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excess Amount (₹)
            </label>

            <input
              type="number"
              value={excessAmount}
              onChange={(e) => setExcessAmount(e.target.value)}
              onWheel={handleWheelPrevent}
              placeholder="Enter excess amount"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border-gray-300"
              step="1"
              min="0"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adjustment Amount (₹)
            </label>

            <input
              type="number"
              value={adjustmentAmount}
              onChange={(e) => setAdjustmentAmount(e.target.value)}
              onWheel={handleWheelPrevent}
              placeholder="Enter adjustment amount"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all border-gray-300"
              step="1"
              min="0"
            />
          </div>
          <div className="mb-6 border border-gray-300 rounded-xl p-5 relative bg-white">
            <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-semibold text-gray-700">
              Excess Adjustment
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adjustment Type
              </label>
              <div className="flex">
                <button
                  type="button"
                  onClick={() => setAdjustmentType("addition")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 transition-all rounded-l-lg ${
                    adjustmentType === "addition"
                      ? "border-green-500 bg-green-50 text-green-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Addition
                </button>

                <button
                  type="button"
                  onClick={() => setAdjustmentType("subtraction")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-l-0 transition-all rounded-r-lg ${
                    adjustmentType === "subtraction"
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <Minus className="w-4 h-4" />
                  Subtraction
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹)
              </label>
              <input
                type="number"
                value={adjustValue}
                onChange={(e) => {
                  setAdjustValue(e.target.value);
                  setError("");
                }}
                onWheel={handleWheelPrevent}
                placeholder="Enter amount"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  error ? "border-red-500" : "border-gray-300"
                }`}
                step="1"
                min="0"
              />
              {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50">
          <Button
            type={ButtonType.Cancel}
            onClick={onClose}
            className="text-xs"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={handleSubmit}
            disabled={isLoading || !adjustValue}
            className="text-xs"
          >
            {isLoading ? "Saving..." : "Save Adjustment"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommissionAdjustmentModal;
